import { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import AccountSection from '../components/settings/AccountSection';
import CreditsSection from '../components/settings/CreditsSection';
import CreditHistorySection from '../components/settings/CreditHistorySection';
import UsageSection from '../components/settings/UsageSection';
import HowItWorksSection from '../components/settings/HowItWorksSection';
import AboutSection from '../components/settings/AboutSection';
import { User, CreditCard, History, BarChart3, HelpCircle, Info } from 'lucide-react';

const sections = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'credits', label: 'Credits', icon: CreditCard },
  { id: 'history', label: 'Credit History', icon: History },
  { id: 'usage', label: 'Usage Statistics', icon: BarChart3 },
  { id: 'how-it-works', label: 'How It Works', icon: HelpCircle },
  { id: 'about', label: 'About', icon: Info },
];

const sectionComponents = {
  account: AccountSection,
  credits: CreditsSection,
  history: CreditHistorySection,
  usage: UsageSection,
  'how-it-works': HowItWorksSection,
  about: AboutSection,
};

export default function Settings() {
  const [activeSection, setActiveSection] = useState('account');
  const ActiveComponent = sectionComponents[activeSection];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Settings
        </h1>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Mobile: horizontal scrolling pills */}
          <div className="md:hidden overflow-x-auto pb-2">
            <div className="flex gap-2 min-w-max">
              {sections.map((section) => {
                const Icon = section.icon;
                const active = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition
                      ${active
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border text-gray-700 dark:text-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Desktop: vertical sidebar nav */}
          <nav className="hidden md:block w-60 flex-shrink-0">
            <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
              {sections.map((section) => {
                const Icon = section.icon;
                const active = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition text-left
                      ${active
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-l-4 border-emerald-500'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 border-l-4 border-transparent'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Content area */}
          <div className="flex-1 min-w-0">
            <ActiveComponent />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
